import { useHistory, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { EditEventProps, Event } from './types';
import { IdParamsType } from 'utils/types';
import { ROUTES } from 'features/routes';
import { getData } from 'services/api';
import { sendGAEvent } from 'utils/utils';
import { updateEvent } from './services';
import { postToast } from 'features/common/utils/postFlash';

export const useEditEventApi = () => {
	const { t } = useTranslation();
	const { push } = useHistory();

	const { id }: IdParamsType = useParams();
	const queryClient = useQueryClient();
	const { data: event } = useQuery({
		queryKey: ['event', id],
		queryFn: (): Promise<Event> => getData(`/events/${id}`),
		select: (eventData: Event) => {
			return {
				...eventData,
				custom_fees: eventData.custom_fees.map(({ taxable, general, ...rest }) => ({
					...rest,
					taxable: taxable ? '1' : '0',
					general: general ? '1' : '0',
				})),
			};
		},
	});

	const { mutate: editEvent } = useMutation<Event, unknown, EditEventProps>(
		async (variables: EditEventProps) => {
			const response = await updateEvent(variables);
			return response.data as Event;
		},
		{
			onSuccess: (_data, variables) => {
				// Downstroke's `event-updated` handler reads only `eventId` (see docs/iframe-communication.md);
				// matches the GeneralSection sender, which edits the same event on the same query key.
				window.parent.postMessage({ action: 'event-updated', eventId: id }, '*');
				sendGAEvent({ category: 'Journeys', action: 'Edit event' });
				// The dashboard the user lands on reads ['event', id]; invalidate so it refetches the edit.
				queryClient.invalidateQueries(['event', id]);
				postToast('success', t('editEvent-saved', 'Show details saved'));
				// Fire the date-change notify (if the form opted in) BEFORE navigating away — a `push`
				// unmounts this page and would cancel an in-flight mutation started after it.
				variables?.onSaved?.();
				push(ROUTES.DASHBOARD_PATH.replace(':id', id));
			},
			onError: () => postToast('error', t('editEvent-save-failed', 'Failed to save show details')),
		}
	);

	return { event, editEvent };
};
